import React from "react";
import { Capacitor } from "@capacitor/core";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { PlayingCard } from "../PlayingCard";
import { DropZone } from "../DropZone";
import { Button } from "../ui/button";
import { t, useLang } from "../../lib/i18n";

// On the website, a little more room between the draw pile and the discard pile (owner's request);
// the apps keep the column as it is.
const WEB_GAP = !Capacitor.isNativePlatform();

/**
 * Left column — Pioche (top), Fausse (below), cancel button.
 *
 * Phases:
 *  - drawing : player must click Pioche OR click Fausse card to draw.
 *  - acting  : player must drag a hand card onto the Fausse to discard.
 */
export const PiocheArea = ({
  game,
  me,
  isMyTurn,
  takenDiscardCard,
  pendingGroups,
  pendingAdditions = [],
  currentPlayerTookDiscard = false,
  canTakeDiscard = true,
  onClickPioche,
  onTakeDiscard,
  onCancelStaging,
  compact = false, // a low phone (lib/tableRoom.js): smaller piles, nothing but the piles
}) => {
  useLang();
  const pileSize = compact ? "sm-5" : "md-5";
  const roomy = WEB_GAP && !compact;
  const showCancel =
    isMyTurn && !me?.eliminated &&
    (pendingGroups.length > 0 || pendingAdditions.length > 0 || takenDiscardCard);

  const drawingPhase = isMyTurn && game.phase === "drawing" && !takenDiscardCard;
  // My turn to draw and the rules let me take the top of the discard pile
  // (see `canTakeDiscard` in Game.jsx). Otherwise the pile shows as closed.
  const takeable = drawingPhase && canTakeDiscard && !currentPlayerTookDiscard;
  const closed   = drawingPhase && !canTakeDiscard && !currentPlayerTookDiscard;
  const actingPhase  = isMyTurn && (game.phase === "acting" || !!takenDiscardCard);
  // Cards drawn under the top one, to show the pile's thickness (2 while the count is unknown).
  const pileCount = game.draw_pile_count;
  const pileDepth = pileCount == null || pileCount > 20 ? 2 : pileCount > 1 ? 1 : 0;

  return (
    <div className={`shrink-0 flex flex-col items-center ${compact ? "gap-1" : "gap-2"} justify-center w-[82px] sm:w-[100px]`}>

      {/* ── Turn banner ───────────────────────────────────────────────── */}
      <AnimatePresence>
        {isMyTurn && (
          <motion.div
            key="turn-banner"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="flex items-center gap-1"
          >
            <motion.div
              className="w-1.5 h-1.5 rounded-full bg-amber-400"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            />
            <span className="text-[8px] text-amber-300 font-semibold uppercase tracking-wide">
              {drawingPhase ? t("pioche.draw") : t("pioche.discard")}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Pioche ───────────────────────────────────────────────────── */}
      <div className={`flex flex-col items-center gap-1 ${roomy ? "mb-2" : ""}`} data-testid="pioche-area" data-keep-selection>
        <span className="text-[9px] sm:text-[10px] text-amber-200/60 uppercase tracking-widest">
          {game.draw_pile_count != null ? t("pioche.pileCount", { n: game.draw_pile_count }) : t("pioche.pile")}
        </span>

        <div className="relative">
          {/* Pulsing ring — drawing phase only */}
          {drawingPhase && (
            <motion.div
              className="absolute -inset-1.5 rounded-xl pointer-events-none"
              animate={{
                boxShadow: [
                  "0 0 0 0px rgba(245,197,66,0.55)",
                  "0 0 0 7px rgba(245,197,66,0)",
                ],
              }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
            />
          )}
          <button
            type="button"
            onClick={onClickPioche}
            disabled={!drawingPhase}
            className={`relative rounded-lg focus:outline-none transition-all duration-150 ${
              drawingPhase
                ? "rami-glow-gold cursor-pointer hover:scale-105 active:scale-95"
                : "opacity-40 cursor-not-allowed"
            }`}
            data-testid="pioche-top"
            id="pioche-pile"
          >
            {/* The pile's thickness: the edges of up to two cards under the top one, fewer as it runs out. */}
            {pileDepth >= 2 && (
              <span aria-hidden="true" className="card-back absolute inset-0 rounded-lg translate-x-[3px] translate-y-[3px] brightness-50" />
            )}
            {pileDepth >= 1 && (
              <span aria-hidden="true" className="card-back absolute inset-0 rounded-lg translate-x-[1.5px] translate-y-[1.5px] brightness-75" />
            )}
            <PlayingCard faceDown size={pileSize} />
            {/* "Prendre" badge overlay */}
            {drawingPhase && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 rounded-b-lg bg-black/60 backdrop-blur-sm py-0.5 flex items-center justify-center gap-0.5 border-t border-amber-400/40">
                <motion.span
                  className="text-[7px] font-bold text-amber-300 uppercase tracking-wide"
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 1.6, repeat: Infinity }}
                >
                  {t("pioche.take")}
                </motion.span>
              </div>
            )}
          </button>
        </div>

        {/* Bounce arrow */}
        {drawingPhase && !compact && (
          <motion.div
            animate={{ y: [0, 3, 0] }}
            transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
            className="text-amber-400/70 text-[10px] leading-none"
          >
            ↓
          </motion.div>
        )}
      </div>

      {/* ── "ou" separator ───────────────────────────────────────────── */}
      <AnimatePresence>
        {drawingPhase && !compact && (
          <motion.div
            key="or-sep"
            initial={{ opacity: 0, scaleX: 0.5 }}
            animate={{ opacity: 1, scaleX: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-1 w-full px-1"
          >
            <div className="flex-1 h-px bg-slate-600/45" />
            <span className="text-[8px] text-slate-400 font-medium">{t("common.or")}</span>
            <div className="flex-1 h-px bg-slate-600/45" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Fausse ───────────────────────────────────────────────────── */}
      <DropZone
        id="discard"
        data={{ type: "discard" }}
        className={[
          "relative flex flex-col items-center gap-0.5 p-1 rounded-xl border-2 transition-all duration-200",
          roomy ? "mt-2" : "",
          actingPhase
            ? "border-amber-400/30 shadow-[0_0_16px_rgba(251,191,36,0.1)] bg-amber-500/4"
            : "border-transparent",
          (drawingPhase || game.discard_locked) ? "opacity-90" : "",
        ].join(" ")}
        activeClassName="border-amber-400/80 bg-amber-500/18 shadow-[0_0_24px_rgba(251,191,36,0.32)]"
        testId="discard-area"
      >
        {/* Acting phase label */}
        <AnimatePresence>
          {actingPhase && (
            <motion.div
              key="discard-hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-0.5"
            >
              <motion.span
                className="text-[7px] text-amber-300/75 font-semibold uppercase tracking-wide"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {t("pioche.discardLabel")}
              </motion.span>
            </motion.div>
          )}
        </AnimatePresence>

        {!actingPhase && (
          <span className="text-[9px] sm:text-[10px] text-amber-200/60 uppercase tracking-widest">
            {game.discard_count != null
              ? t("pioche.fausseCount", { n: Math.max(0, game.discard_count - (currentPlayerTookDiscard ? 1 : 0)) })
              : t("pioche.fausse")}
          </span>
        )}

        {game.discard_top && !takenDiscardCard ? (
          <div className="relative" id="defausse-pile">
            <div
              className={`relative transition-all ${
                currentPlayerTookDiscard
                  ? "opacity-40 grayscale pointer-events-none"
                  : takeable
                  ? "rami-glow-gold cursor-pointer hover:scale-105"
                  : closed
                  ? "opacity-70"
                  : "opacity-90"
              }`}
              data-testid="take-discard-btn"
            >
              <PlayingCard
                card={game.discard_top}
                size={pileSize}
                disabled={!takeable}
                onClick={takeable ? onTakeDiscard : undefined}
                testId="discard-top"
              />
              {/* "Prendre" badge — only when the active player may take it */}
              {takeable && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 rounded-b-lg bg-black/60 backdrop-blur-sm py-0.5 flex items-center justify-center border-t border-amber-400/40">
                  <motion.span
                    className="text-[7px] font-bold text-amber-300 uppercase tracking-wide"
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 1.6, repeat: Infinity, delay: 0.3 }}
                  >
                    {t("pioche.take")}
                  </motion.span>
                </div>
              )}
              {/* "Fermée" badge — my turn to draw, but this card cannot be taken */}
              {closed && (
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 rounded-b-lg bg-black/70 backdrop-blur-sm py-0.5 flex items-center justify-center border-t border-slate-500/40"
                  data-testid="discard-closed-badge"
                >
                  <span className="text-[7px] font-bold text-slate-300 uppercase tracking-wide">{t("pioche.closed")}</span>
                </div>
              )}
              {/* "Prise" overlay — card is being staged by the active opponent */}
              {currentPlayerTookDiscard && (
                <div className="absolute inset-0 rounded-lg flex flex-col items-center justify-center gap-0.5 bg-black/30">
                  <span className="text-[9px] font-bold text-amber-300 uppercase tracking-wide">{t("pioche.taken")}</span>
                </div>
              )}
            </div>

            {/* Acting phase: drag-to-discard hint */}
            {actingPhase && !currentPlayerTookDiscard && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute -bottom-4 inset-x-0 flex items-center justify-center gap-0.5"
              >
                <span className="text-[7px] text-amber-300/65">{t("pioche.drag")}</span>
              </motion.div>
            )}
          </div>
        ) : (
          <div className={`relative ${compact ? "w-[42px] h-[61px]" : "w-[53px] h-[76px]"} rounded-lg border-2 flex flex-col items-center justify-center gap-1 ${
            actingPhase
              ? "border-amber-400/40 border-dashed"
              : "border-dashed border-white/15"
          }`}>
            {/* Radial pulse for acting phase empty slot */}
            {actingPhase && (
              <motion.div
                className="absolute inset-0 rounded-lg pointer-events-none"
                animate={{ opacity: [0, 0.18, 0] }}
                transition={{ duration: 2.2, repeat: Infinity }}
                style={{
                  background: "radial-gradient(circle at center, rgba(251,191,36,0.35) 0%, transparent 70%)",
                }}
              />
            )}

            {actingPhase ? (
              <>
                <span className="text-[7px] text-amber-300/65 font-medium relative z-10 text-center leading-tight px-1">
                  {t("pioche.dropHere")}
                </span>
              </>
            ) : (
              <span className="text-white/30 text-[10px]">
                {takenDiscardCard ? t("pioche.taken") : t("pioche.empty")}
              </span>
            )}
          </div>
        )}
      </DropZone>

      {/* ── Cancel staging ────────────────────────────────────────────── */}
      {showCancel && (
        <Button
          data-testid="side-cancel-btn"
          onClick={onCancelStaging}
          size="sm"
          variant="outline"
          className="relative z-50 border-red-500/40 text-red-300 rounded-full text-[10px] w-full mt-1 bg-slate-950/80 backdrop-blur-sm"
        >
          <X className="h-3 w-3 mr-1" /> {t("pioche.cancel")}
        </Button>
      )}
    </div>
  );
};

export default PiocheArea;
